import React, { useState } from "react";
import { Box, Typography, Checkbox, InputBase } from "@mui/material";
import {
  ChevronDown,
  ChevronUp,
  Search,
  Building2,
  Gift,
  Users,
  MapPin,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { FormattedMessage, useIntl } from "react-intl";
import {
  companyIndustryMap,
  allBenefitKeys,
  companySizeMap,
  companyLocationCities,
  companyLocationProvinceGroups,
  companyLocationProvinceKeys,
} from "bundles/common/utils/constants";

const FilterSection = ({
  title,
  icon,
  items,
  selected,
  onChange,
  defaultOpen = false,
  searchable = false, // Add a search bar to search a certain filter value
  searchPlaceholder,
  renderLabel,
  primaryCount = 5, // How many items to show before the "see more" toggle
}) => {
  const intl = useIntl();
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredItems =
    searchable && searchQuery
      ? items.filter((item) =>
        (renderLabel ? renderLabel(item) : item)
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      )
      : items;

  const visibleItems = showAll ? filteredItems : filteredItems.slice(0, primaryCount);
  const hasMore = filteredItems.length > primaryCount;

  const toggle = (item) => {
    onChange(
      selected.includes(item)
        ? selected.filter((s) => s !== item)
        : [...selected, item]
    );
  };

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: 1,
        borderColor: "hsla(160, 10%, 90%, 0.6)",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Section header */}
      <Box
        component="button"
        onClick={() => setOpen(!open)}
        sx={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1.75,
          fontSize: "0.875rem",
          fontFamily: "Segoe UI Semibold",
          color: "hsl(178, 93%, 10%)",
          bgcolor: "transparent",
          border: "none",
          cursor: "pointer",
          transition: "background-color 0.2s",
          "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.4)" },
        }}
      >
        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <Box
            component="span"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 28,
              height: 28,
              borderRadius: "8px",
              bgcolor: "hsla(153, 68%, 25%, 0.1)",
              color: "hsl(153, 68%, 25%)",
            }}
          >
            {icon}
          </Box>
          {title}
          {selected.length > 0 && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 20,
                height: 20,
                px: 0.75,
                borderRadius: "9999px",
                bgcolor: "hsl(153, 68%, 25%)",
                color: "#fff",
                fontSize: "10px",
                fontWeight: 700,
              }}
            >
              {selected.length}
            </Box>
          )}
        </Box>
        {open ? (
          <ChevronUp size={14} style={{ color: "hsl(178 10% 45%)" }} />
        ) : (
          <ChevronDown size={14} style={{ color: "hsl(178 10% 45%)" }} />
        )}
      </Box>

      {/* Section content */}
      <AnimatePresence>
        {open && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            sx={{ overflow: "hidden" }}
          >
            <Box sx={{ px: 1.75, pb: 1.75, display: "flex", flexDirection: "column", gap: 0.5 }}>
              {/* Search input */}
              {searchable && (
                <Box sx={{ position: "relative", mb: 1 }}>
                  <Search
                    size={13}
                    style={{
                      position: "absolute",
                      left: "10px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "hsl(178 10% 45%)",
                    }}
                  />
                  <InputBase
                    fullWidth
                    placeholder={searchPlaceholder || intl.formatMessage({ id: "company_index.search_placeholder" })}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                      fontSize: "0.75rem",
                      fontFamily: "Segoe UI Regular",
                      bgcolor: "hsla(160, 10%, 96%, 0.4)",
                      border: 1,
                      borderColor: "hsla(160, 10%, 90%, 0.5)",
                      borderRadius: "8px",
                      transition: "all 0.2s",
                      "&:focus-within": {
                        borderColor: "hsla(153, 68%, 25%, 0.3)",
                        boxShadow: "0 0 0 1px hsla(153, 68%, 25%, 0.3)",
                      },
                      "& .MuiInputBase-input": {
                        pl: "32px",
                        pr: 1.5,
                        py: 1,
                        "&::placeholder": {
                          color: "hsla(178, 10%, 45%, 0.6)",
                          opacity: 1,
                        },
                      },
                    }}
                  />
                </Box>
              )}

              {/* Selected items as chips */}
              {selected.length > 0 && !searchQuery && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.75,
                    pb: 1,
                    mb: 1,
                    borderBottom: 1,
                    borderColor: "hsla(160, 10%, 90%, 0.5)",
                  }}
                >
                  {selected.map((item) => (
                    <Box
                      component="button"
                      key={item}
                      onClick={() => toggle(item)}
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                        px: 1.25,
                        py: 0.5,
                        fontSize: "11px",
                        fontWeight: 500,
                        fontFamily: "Segoe UI Regular",
                        bgcolor: "hsla(153, 68%, 25%, 0.15)",
                        color: "hsl(153, 68%, 25%)",
                        borderRadius: "9999px",
                        border: "none",
                        cursor: "pointer",
                        transition: "background-color 0.2s",
                        "&:hover": { bgcolor: "hsla(153, 68%, 25%, 0.2)" },
                      }}
                    >
                      {renderLabel ? renderLabel(item) : item}
                      <Box component="span" sx={{ color: "hsla(153, 68%, 25%, 0.5)" }}>
                        ×
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Checkbox list */}
              <Box>
                {visibleItems.map((item) => (
                  <Box
                    component="label"
                    key={item}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      py: "7px",
                      px: 0.75,
                      borderRadius: "8px",
                      cursor: "pointer",
                      transition: "background-color 0.2s",
                      "&:hover": { bgcolor: "hsla(160, 10%, 96%, 0.4)" },
                      "&:hover .filter-label": { color: "hsl(178, 93%, 10%)" },
                    }}
                  >
                    <Checkbox
                      checked={selected.includes(item)}
                      onChange={() => toggle(item)}
                      size="small"
                      sx={{
                        p: 0,
                        "&.Mui-checked": {
                          color: "hsl(153, 68%, 25%)",
                        },
                      }}
                    />
                    <Typography
                      className="filter-label"
                      sx={{
                        fontSize: "13px",
                        color: "hsla(178, 93%, 10%, 0.8)",
                        fontFamily: "Segoe UI Regular",
                        transition: "color 0.2s",
                        flex: 1,
                      }}
                    >
                      {renderLabel ? renderLabel(item) : item}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Show more / less */}
              {hasMore && !searchQuery && (
                <Box
                  component="button"
                  onClick={() => setShowAll(!showAll)}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    fontFamily: "Segoe UI Semibold",
                    color: "hsl(153, 68%, 25%)",
                    mt: 0.5,
                    px: 0.75,
                    border: "none",
                    bgcolor: "transparent",
                    cursor: "pointer",
                    textAlign: "left",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  {showAll
                    ? intl.formatMessage({ id: "company_index.show_less" })
                    : intl.formatMessage({ id: "company_index.show_all" }, { count: filteredItems.length - primaryCount })}
                </Box>
              )}

              {/* No results */}
              {searchable && searchQuery && filteredItems.length === 0 && (
                <Typography
                  sx={{
                    fontSize: "0.75rem",
                    color: "hsl(178, 10%, 45%)",
                    py: 1,
                    px: 0.75,
                    fontFamily: "Segoe UI Regular",
                  }}
                >
                  <FormattedMessage id="company_index.no_filter_results" />
                </Typography>
              )}
            </Box>
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};

const FilterSidebar = ({
  selectedIndustries,
  setSelectedIndustries,
  // selectedSubSectors,
  // setSelectedSubSectors,
  selectedBenefits,
  setSelectedBenefits,
  selectedSizes,
  setSelectedSizes,
  selectedLocations,
  setSelectedLocations,
  onClearAll,
  totalActiveFilters,
}) => {
  const intl = useIntl();

  // Provinces of the selected language are shown by default; the other-language provinces
  // appear under "see more". Paris is appended only for fr-fr.
  const { primary: primaryProvinces, secondary: secondaryProvinces } =
    companyLocationProvinceGroups(intl.locale);
  const locationOptions = [
    ...primaryProvinces,
    ...secondaryProvinces,
    ...companyLocationCities(intl.locale),
  ];
  const renderLocationLabel = (item) =>
    companyLocationProvinceKeys.has(item)
      ? intl.formatMessage({ id: `regions.${item}` })
      : intl.formatMessage({ id: `company_index.cities.${item}` });

  // const availableSubSectors =
  //   selectedIndustries.length > 0
  //     ? selectedIndustries.flatMap((s) => companySubSectorMap[s] || [])
  //     : Object.values(companySubSectorMap).flat();

  return (
    <Box component="aside" sx={{ width: "100%" }}>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <Typography
          sx={{
            fontSize: "0.875rem",
            fontWeight: 700,
            fontFamily: "Segoe UI Bold",
            color: "hsl(178, 93%, 10%)",
            alignItems: "center",
            gap: 1,
            display: { xs: "none", sm: "flex" },
          }}
        >
          <FormattedMessage id="company_index.filters" />
          {totalActiveFilters > 0 && (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                px: 1,
                py: 0.25,
                borderRadius: "9999px",
                bgcolor: "hsl(153, 68%, 25%)",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 700,
              }}
            >
              {totalActiveFilters}
            </Box>
          )}
        </Typography>
        {totalActiveFilters > 0 && (
          <Box
            component="button"
            onClick={onClearAll}
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              fontFamily: "Segoe UI Regular",
              color: "hsl(178, 10%, 45%)",
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              transition: "color 0.2s",
              "&:hover": { color: "hsl(0, 84.2%, 60.2%)" },
            }}
          >
            <FormattedMessage id="company_index.clear_all" />
          </Box>
        )}
      </Box>

      {/* Filter sections */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
        <FilterSection
          title={intl.formatMessage({ id: "candidate.desired_sector", defaultMessage: "Secteur" })}
          icon={<Building2 size={14} />}
          items={companyIndustryMap}
          selected={selectedIndustries}
          onChange={setSelectedIndustries}
          defaultOpen={true}
          renderLabel={(item) =>
            intl.formatMessage({ id: `company.company_industry.${item}` })
          }
        />

        {/* <FilterSection
          title={intl.formatMessage({ id: "company_index.sub_sector" })}
          icon={<Layers size={14} />}
          items={availableSubSectors}
          selected={selectedSubSectors}
          onChange={setSelectedSubSectors}
          searchable={true}
          searchPlaceholder={intl.formatMessage({ id: "company_index.search_sub_sector" })}
          renderLabel={(item) =>
            intl.formatMessage({ id: `company.company_sub_sector.${item}` })
          }
        /> */}

        <FilterSection
          title={intl.formatMessage({ id: "company_index.benefits" })}
          icon={<Gift size={14} />}
          items={allBenefitKeys}
          selected={selectedBenefits}
          onChange={setSelectedBenefits}
          defaultOpen={true}
          searchable={true}
          searchPlaceholder={intl.formatMessage({ id: "company_index.search_benefit" })}
          renderLabel={(item) =>
            intl.formatMessage({ id: `employee_benefits.${item}` })
          }
        />

        <FilterSection
          title={intl.formatMessage({ id: "company_index.company_size" })}
          icon={<Users size={14} />}
          items={companySizeMap}
          selected={selectedSizes}
          onChange={setSelectedSizes}
          defaultOpen={true}
          renderLabel={(item) =>
            intl.formatMessage({ id: `company.company_size.options.${item}` })
          }
        />

        <FilterSection
          title={intl.formatMessage({ id: "company_index.location" })}
          icon={<MapPin size={14} />}
          items={locationOptions}
          selected={selectedLocations}
          onChange={setSelectedLocations}
          defaultOpen={true}
          primaryCount={primaryProvinces.length}
          renderLabel={renderLocationLabel}
        />
      </Box>
    </Box>
  );
};

export default FilterSidebar;
